import { useCallback, useEffect, useState } from "react";
import {
  getStrategies, createStrategy, updateStrategy, deleteStrategy, seedStrategies,
  getRules, addRule, updateRule, deleteRule, runScreener,
} from "./StrategyService";

const RULE_TYPES = [
  { value: "RSI_BELOW",          label: "RSI below threshold",     hasThreshold: true,  desc: "Fires when RSI(14) drops below the given level" },
  { value: "RSI_ABOVE",          label: "RSI above threshold",     hasThreshold: true,  desc: "Fires when RSI(14) rises above the given level" },
  { value: "PRICE_ABOVE_SMA",    label: "Price above SMA",         hasPeriod: true,     desc: "Fires when price is above SMA(period)" },
  { value: "PRICE_BELOW_SMA",    label: "Price below SMA",         hasPeriod: true,     desc: "Fires when price is below SMA(period)" },
  { value: "SMA_GOLDEN_CROSS",   label: "Golden cross (SMA20/50)", desc: "SMA(20) just crossed above SMA(50) — bullish" },
  { value: "SMA_DEATH_CROSS",    label: "Death cross (SMA20/50)",  desc: "SMA(20) just crossed below SMA(50) — bearish" },
  { value: "MACD_BULLISH_CROSS", label: "MACD bullish cross",      desc: "MACD line crossed above signal line" },
  { value: "MACD_BEARISH_CROSS", label: "MACD bearish cross",      desc: "MACD line crossed below signal line" },
  { value: "PROFIT_TARGET",      label: "Profit target hit",       hasPct: true,        desc: "Fires when unrealised gain exceeds pct% (SELL)" },
  { value: "STOP_LOSS",          label: "Stop-loss hit",           hasPct: true,        desc: "Fires when unrealised loss exceeds pct% (SELL)" },
  { value: "CUSTOM",             label: "Custom / learned rule",   isCustom: true,      desc: "Your own rule — describe what you've learned" },
];

const SIGNAL_BADGE = { BUY: "stocks-good", SELL: "stocks-bad" };

const signalEmoji = { BUY: "🟢", SELL: "🔴", undefined: "⚪" };

export default function TradingStrategyPage({ onOpenChart }) {
  const [strategies,     setStrategies]     = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selected,       setSelected]       = useState(null);
  const [rules,          setRules]          = useState([]);
  const [rulesLoading,   setRulesLoading]   = useState(false);
  const [screenerData,   setScreenerData]   = useState([]);
  const [screenerLoading,setScreenerLoading]= useState(false);
  const [view,           setView]           = useState("strategies"); // strategies | screener
  const [showNewStrat,   setShowNewStrat]   = useState(false);
  const [showNewRule,    setShowNewRule]     = useState(false);
  const [editingRule,    setEditingRule]     = useState(null);

  // Strategy form
  const [stratName, setStratName]   = useState("");
  const [stratDesc, setStratDesc]   = useState("");
  const [stratTags, setStratTags]   = useState("");

  // Rule form
  const [ruleSignal,   setRuleSignal]   = useState("BUY");
  const [ruleType,     setRuleType]     = useState("RSI_BELOW");
  const [ruleThresh,   setRuleThresh]   = useState("30");
  const [rulePeriod,   setRulePeriod]   = useState("20");
  const [rulePct,      setRulePct]      = useState("5");
  const [ruleDesc,     setRuleDesc]     = useState("");
  const [ruleLearnedOn,setRuleLearnedOn]= useState("");

  const loadStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getStrategies();
      setStrategies(list || []);
      if (!selected && list?.length > 0) setSelected(list[0].id);
    } finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { loadStrategies(); }, [loadStrategies]);

  const loadRules = useCallback(async (sid) => {
    if (!sid) return;
    setRulesLoading(true);
    try { setRules(await getRules(sid)); }
    finally { setRulesLoading(false); }
  }, []);

  useEffect(() => { loadRules(selected); }, [selected, loadRules]);

  const handleSeed = async () => {
    const list = await seedStrategies();
    setStrategies(list || []);
    if (list?.length > 0) setSelected(list[0].id);
  };

  const handleCreateStrategy = async () => {
    if (!stratName.trim()) return;
    const s = await createStrategy({ name: stratName, description: stratDesc, tags: stratTags });
    setStrategies(prev => [s, ...prev]);
    setSelected(s.id);
    setShowNewStrat(false); setStratName(""); setStratDesc(""); setStratTags("");
  };

  const handleToggleActive = async (s) => {
    const updated = await updateStrategy(s.id, { active: !s.active });
    setStrategies(prev => prev.map(x => x.id === s.id ? updated : x));
  };

  const handleDeleteStrategy = async (id) => {
    if (!window.confirm("Delete this strategy and all its rules?")) return;
    await deleteStrategy(id);
    setStrategies(prev => prev.filter(s => s.id !== id));
    if (selected === id) setSelected(strategies.find(s => s.id !== id)?.id || null);
  };

  const buildParams = () => {
    const rt = RULE_TYPES.find(r => r.value === ruleType);
    if (rt?.hasThreshold) return JSON.stringify({ threshold: parseFloat(ruleThresh) });
    if (rt?.hasPeriod)    return JSON.stringify({ period: parseInt(rulePeriod) });
    if (rt?.hasPct)       return JSON.stringify({ pct: parseFloat(rulePct) });
    return "{}";
  };

  const openRuleForm = (rule) => {
    if (rule) {
      setEditingRule(rule);
      setRuleSignal(rule.signal);
      setRuleType(rule.ruleType);
      setRuleDesc(rule.description || "");
      setRuleLearnedOn(rule.learnedOn || "");
      try {
        const p = JSON.parse(rule.parameters || "{}");
        if (p.threshold) setRuleThresh(String(p.threshold));
        if (p.period)    setRulePeriod(String(p.period));
        if (p.pct)       setRulePct(String(p.pct));
      } catch {}
    } else {
      setEditingRule(null);
      setRuleSignal("BUY"); setRuleType("RSI_BELOW"); setRuleThresh("30");
      setRulePeriod("20"); setRulePct("5"); setRuleDesc(""); setRuleLearnedOn("");
    }
    setShowNewRule(true);
  };

  const handleSaveRule = async () => {
    const data = {
      signal: ruleSignal, ruleType,
      parameters: buildParams(),
      description: ruleDesc || null,
      learnedOn: ruleLearnedOn || null,
    };
    if (editingRule) {
      await updateRule(selected, editingRule.id, data);
    } else {
      await addRule(selected, data);
    }
    setShowNewRule(false);
    await loadRules(selected);
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Delete this rule?")) return;
    await deleteRule(selected, ruleId);
    await loadRules(selected);
  };

  const handleScreener = async () => {
    setScreenerLoading(true);
    try { setScreenerData(await runScreener()); }
    finally { setScreenerLoading(false); }
  };

  useEffect(() => {
    if (view === "screener") handleScreener();
  }, [view]);

  const selectedStrategy = strategies.find(s => s.id === selected);
  const rtInfo = RULE_TYPES.find(r => r.value === ruleType) || {};

  return (
    <div className="stocks-section">
      {/* Header + tabs */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span className="card-title" style={{ flex: 1, margin: 0 }}>🎯 Trading Strategies</span>
        <button className="btn btn-ghost btn-sm" onClick={() => setView("strategies")}>My Strategies</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setView("screener")}>📡 Screener</button>
      </div>

      {/* ── Strategy list view ────────────────────────────────────────────────── */}
      {view === "strategies" && (
        <>
          {strategies.length === 0 && !loading && (
            <div className="card" style={{ textAlign: "center", padding: 32 }}>
              <p style={{ color: "var(--color-muted)", marginBottom: 16 }}>
                No strategies yet. Load the 3 beginner starter strategies or create your own.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button className="btn" onClick={handleSeed}>🚀 Load Starter Strategies</button>
                <button className="btn btn-ghost" onClick={() => setShowNewStrat(true)}>+ Custom</button>
              </div>
            </div>
          )}

          {strategies.length > 0 && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 200, maxWidth: 280 }}>
                {strategies.map(s => (
                  <button key={s.id}
                    style={{ textAlign: "left", padding: "8px 12px", borderRadius: 8,
                             border: `1px solid ${selected === s.id ? "var(--color-accent)" : "var(--color-border)"}`,
                             background: selected === s.id ? "var(--color-accent)1a" : "var(--color-surface)",
                             cursor: "pointer" }}
                    onClick={() => setSelected(s.id)}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{s.name}</div>
                    {s.tags && <div style={{ fontSize: 11, color: "var(--color-muted)" }}>{s.tags}</div>}
                    {!s.active && <span style={{ fontSize: 10, color: "var(--color-muted)" }}>inactive</span>}
                  </button>
                ))}
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }} onClick={() => setShowNewStrat(true)}>+ New strategy</button>
              </div>

              {/* Strategy detail */}
              {selectedStrategy && (
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, fontSize: 15 }}>{selectedStrategy.name}</div>
                      {selectedStrategy.description && (
                        <div style={{ color: "var(--color-muted)", fontSize: 13, marginTop: 2 }}>{selectedStrategy.description}</div>
                      )}
                      {selectedStrategy.tags && (
                        <div style={{ marginTop: 4, display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {selectedStrategy.tags.split(",").map(t => (
                            <span key={t} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 99, background: "var(--color-accent)22", color: "var(--color-accent)" }}>{t.trim()}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="btn btn-ghost btn-sm"
                      onClick={() => handleToggleActive(selectedStrategy)}>
                      {selectedStrategy.active ? "Deactivate" : "Activate"}
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ color: "var(--color-danger)" }}
                      onClick={() => handleDeleteStrategy(selected)}>Delete</button>
                  </div>

                  {/* Rules */}
                  <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, flex: 1 }}>Rules ({rules.length})</span>
                    <button className="btn btn-sm" onClick={() => openRuleForm(null)}>+ Add rule</button>
                  </div>

                  {rulesLoading && <div>Loading rules…</div>}

                  {rules.length === 0 && !rulesLoading && (
                    <div style={{ color: "var(--color-muted)", padding: 8 }}>No rules yet. Add rules to define when to BUY or SELL.</div>
                  )}

                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {rules.map(r => {
                      const rt = RULE_TYPES.find(x => x.value === r.ruleType);
                      return (
                        <div key={r.id} style={{
                          border: "1px solid var(--color-border)", borderRadius: 8, padding: "8px 12px",
                          borderLeft: `3px solid ${r.signal === "BUY" ? "var(--color-success)" : "var(--color-danger)"}`,
                          background: "var(--color-surface)"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span className={SIGNAL_BADGE[r.signal]} style={{ fontWeight: 700, fontSize: 11 }}>
                              {signalEmoji[r.signal]} {r.signal}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>{rt?.label || r.ruleType}</span>
                            {r.ruleType === "CUSTOM" && <span style={{ fontSize: 11, color: "var(--color-accent)" }}>⭐ Learned</span>}
                            {r.learnedOn && <span style={{ fontSize: 10, color: "var(--color-muted)" }}>learned {r.learnedOn}</span>}
                            <div style={{ flex: 1 }} />
                            <button className="btn btn-ghost btn-sm" onClick={() => openRuleForm(r)}>Edit</button>
                            <button className="btn btn-ghost btn-sm" style={{ color: "var(--color-danger)" }} onClick={() => handleDeleteRule(r.id)}>✕</button>
                          </div>
                          {r.description && <div style={{ fontSize: 12, color: "var(--color-muted)", marginTop: 4 }}>{r.description}</div>}
                          {r.parameters && r.parameters !== "{}" && (
                            <div style={{ fontSize: 11, color: "var(--color-muted)", marginTop: 2 }}>
                              Params: {r.parameters}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Screener view ─────────────────────────────────────────────────────── */}
      {view === "screener" && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <span style={{ color: "var(--color-muted)", fontSize: 13 }}>
              Evaluates all active strategy rules against your watchlist stocks.
            </span>
            <button className="btn btn-sm" onClick={handleScreener} disabled={screenerLoading}>
              {screenerLoading ? "Running…" : "↻ Refresh"}
            </button>
          </div>

          {screenerLoading && <div>Evaluating strategies…</div>}

          {!screenerLoading && screenerData.length === 0 && (
            <div style={{ color: "var(--color-muted)", padding: 16 }}>
              No signals yet. Make sure you have active strategies with rules AND stocks in your Watchlist with price history loaded.
            </div>
          )}

          {screenerData.map(stock => (
            <div key={stock.symbol} className="card" style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 800, fontSize: 15 }}>{stock.symbol}</span>
                {stock.stockName && <span style={{ color: "var(--color-muted)", fontSize: 13 }}>{stock.stockName}</span>}
                {stock.latestPrice && <span style={{ fontWeight: 600 }}>₹{Number(stock.latestPrice).toFixed(2)}</span>}
                {stock.priceDate && <span style={{ color: "var(--color-muted)", fontSize: 11 }}>as of {stock.priceDate}</span>}
                {stock.buyCount > 0 && (
                  <span className="stocks-good" style={{ fontSize: 12, padding: "2px 7px", borderRadius: 99, background: "rgba(34,197,94,0.12)" }}>
                    🟢 {stock.buyCount} BUY signal{stock.buyCount > 1 ? "s" : ""}
                  </span>
                )}
                {stock.sellCount > 0 && (
                  <span className="stocks-bad" style={{ fontSize: 12, padding: "2px 7px", borderRadius: 99, background: "rgba(239,68,68,0.12)" }}>
                    🔴 {stock.sellCount} SELL signal{stock.sellCount > 1 ? "s" : ""}
                  </span>
                )}
                <div style={{ flex: 1 }} />
                {onOpenChart && (
                  <button className="btn btn-ghost btn-sm" onClick={() => onOpenChart(stock.symbol, stock.stockName)}>View chart</button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {stock.signals?.filter(s => s.triggered).map((sig, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "flex-start", gap: 8,
                    padding: "6px 10px", borderRadius: 6,
                    background: sig.signal === "BUY" ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)",
                    borderLeft: `3px solid ${sig.signal === "BUY" ? "#22c55e" : "#ef4444"}`
                  }}>
                    <span style={{ fontWeight: 700, fontSize: 11 }}>{signalEmoji[sig.signal]} {sig.signal}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600 }}>{sig.strategyName} — {RULE_TYPES.find(r => r.value === sig.ruleType)?.label || sig.ruleType}</div>
                      <div style={{ fontSize: 11, color: "var(--color-muted)" }}>{sig.reason}</div>
                    </div>
                    {sig.indicatorValue != null && (
                      <span style={{ fontSize: 11, color: "var(--color-muted)" }}>{Number(sig.indicatorValue).toFixed(2)}</span>
                    )}
                  </div>
                ))}
                {stock.signals?.filter(s => !s.triggered).length > 0 && (
                  <details>
                    <summary style={{ fontSize: 11, color: "var(--color-muted)", cursor: "pointer" }}>
                      {stock.signals.filter(s => !s.triggered).length} rule{stock.signals.filter(s => !s.triggered).length > 1 ? "s" : ""} not triggered
                    </summary>
                    {stock.signals.filter(s => !s.triggered).map((sig, i) => (
                      <div key={i} style={{ fontSize: 11, color: "var(--color-muted)", padding: "4px 10px" }}>
                        ⚪ {sig.signal} — {RULE_TYPES.find(r => r.value === sig.ruleType)?.label || sig.ruleType}: {sig.reason}
                      </div>
                    ))}
                  </details>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── New Strategy modal ────────────────────────────────────────────────── */}
      {showNewStrat && (
        <div className="modal-overlay" onClick={() => setShowNewStrat(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">New Strategy</div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={stratName} onChange={e => setStratName(e.target.value)} placeholder="e.g. My RSI Strategy" />
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={3} value={stratDesc} onChange={e => setStratDesc(e.target.value)} placeholder="What is this strategy about?" />
            <label className="form-label">Tags (comma-separated)</label>
            <input className="form-input" value={stratTags} onChange={e => setStratTags(e.target.value)} placeholder="swing,momentum,beginner" />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleCreateStrategy}>Create</button>
              <button className="btn btn-ghost" onClick={() => setShowNewStrat(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Rule form modal ───────────────────────────────────────────────────── */}
      {showNewRule && (
        <div className="modal-overlay" onClick={() => setShowNewRule(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-title">{editingRule ? "Edit Rule" : "Add Rule"}</div>

            <label className="form-label">Signal</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {["BUY", "SELL"].map(s => (
                <button key={s} className={`btn btn-sm ${ruleSignal === s ? "" : "btn-ghost"}`}
                  style={ruleSignal === s ? { background: s === "BUY" ? "#22c55e" : "#ef4444" } : {}}
                  onClick={() => setRuleSignal(s)}>{s}</button>
              ))}
            </div>

            <label className="form-label">Rule type</label>
            <select className="form-input" value={ruleType} onChange={e => setRuleType(e.target.value)}>
              {RULE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {rtInfo.desc && <div style={{ fontSize: 11, color: "var(--color-muted)", marginTop: 4, marginBottom: 8 }}>{rtInfo.desc}</div>}

            {rtInfo.hasThreshold && (
              <>
                <label className="form-label">Threshold</label>
                <input className="form-input" type="number" value={ruleThresh}
                  onChange={e => setRuleThresh(e.target.value)}
                  placeholder={ruleSignal === "BUY" ? "e.g. 30 (oversold)" : "e.g. 70 (overbought)"} />
              </>
            )}
            {rtInfo.hasPeriod && (
              <>
                <label className="form-label">SMA period</label>
                <input className="form-input" type="number" value={rulePeriod}
                  onChange={e => setRulePeriod(e.target.value)} placeholder="e.g. 20" />
              </>
            )}
            {rtInfo.hasPct && (
              <>
                <label className="form-label">Percentage (%)</label>
                <input className="form-input" type="number" value={rulePct}
                  onChange={e => setRulePct(e.target.value)}
                  placeholder={ruleType === "PROFIT_TARGET" ? "e.g. 15 (take profit at 15%)" : "e.g. 5 (stop-loss at 5%)"} />
              </>
            )}

            <label className="form-label">Description / what I learned</label>
            <textarea className="form-input" rows={3} value={ruleDesc} onChange={e => setRuleDesc(e.target.value)}
              placeholder="Explain WHY this rule works, what you observed, or when to apply it…" />

            <label className="form-label">Date learned (optional)</label>
            <input className="form-input" type="date" value={ruleLearnedOn}
              onChange={e => setRuleLearnedOn(e.target.value)} />

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleSaveRule}>Save rule</button>
              <button className="btn btn-ghost" onClick={() => setShowNewRule(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
